/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import {
  InvoiceOperationEnum,
  InvoiceStatus,
  Marketplace,
  MarketplaceConfig,
  PaginationInfo,
  PermissionEnum,
  PullAccountsInput,
  RestartInvoicesInput,
  ShopSyncEntityType,
  ShopSyncStatusEnum,
} from "TypesAndInterfaces";
import {
  GET_INVOICE_LOGS,
  GET_SHOP_LOGS,
  GET_OLD_INVOICE_LOGS,
  GET_OLD_SHOP_LOGS,
  LogOutput,
  InvoiceLogsFilter,
  ShopLogsFilter,
} from "../queries";
import Button from "components/Button/Button";
import Dropdown from "components/Dropdown/Dropdown";
import InputField from "components/InputField/InputField";
import "./ShopInvoice.scss";
import "react-tabs/style/react-tabs.css";
import Modal from "components/Modal/Modal";
import TextArea from "components/TextArea/TextArea";
import { displayError, displaySuccess } from "utils/displayNotification";
import { toast } from "react-toastify";
import { useNavigate, createSearchParams } from "react-router-dom";
import { getRoutePath } from 'routes/route-utils';
import { AdyenRoutesName } from 'routes/route-names';
import ControlSyncs from "pages/MarketplaceControl/components/ControlSyncs";
import { initialState } from "utils/validation";
import { restartInvoicesMutation, retryInvoicesMutation, syncShopAccountsMutation } from "pages/MarketplaceControl/graphql";
import { getMarketplaceQuery } from "pages/Marketplaces/queries";
import usePermission from "hooks/usePermission";
import ToggleButton from "components/ToggleButton/ToggleButton";

enum LogTabPages {
  Invoice,
  Shop,
  Activity
}
const MAX_ROWS = 10;

export const ShopInvoiceLogs = () => {
  const today = new Date();
  const oneMonthBefore = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  //#region hooks
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<LogOutput[]>([]);
  const [logsPerPage, setLogsPerPage] = useState(10);
  const [index, setIndex] = useState(1);
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceLogsFilter>({
    isAscending: false,
    fromDate: oneMonthBefore,
  });
  const [shopFilter, setShopFilter] = useState<ShopLogsFilter>({
    fromDate: oneMonthBefore,
    isAscending: false,
  });
  const selectinvoiceRef = useRef(null);
  const selectTypeRef = useRef(null);
  const invoiceRef = useRef(null);
  const shopRef = useRef(null);
  const [currentTab, setCurrentTab] = useState(LogTabPages.Invoice);
  const [marketplaceName, setMarketplaceName] = useState("");
  const [logModalOpened, setLogModalOpened] = useState(false);
  const [selectedLog, setSelectedLog] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [update, setUpdate] = useState(new Date());
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [marketplaceConfig, setMarketplaceConfig] =
    useState<MarketplaceConfig>(initialState);
  const [marketplaceId, setMarketplaceId] = useState<number>();
  const [disabled, setDisabled] = useState(false);
  const { isAuthorized } = usePermission(PermissionEnum.ADMIN);
  const [isAllowOldRecordsModalOpened, setIsAllowOldRecordsModalOpened] = useState(false);
  const [isAllowOldRecordsActive, setIsAllowOldRecordsActive] = useState(false);
  const [maxDate, setMaxDate] = useState<Date>(today);
  const [minDate, setMinDate] = useState<Date>();
  const [isOldRecordsActive, setIsOldRecordsActive] = useState(false);
  const [totalRows, setTotalRows] = useState(logsPerPage + MAX_ROWS);
  const DELAY = 400;

  const [showInvoiceTable, setShowInvoiceTable] = useState(false);
  const [showShopTable, setShowShopTable] = useState(false);
  const invoiceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  //#endregion

  const navigate = useNavigate();

  //#region API CALLS
  const [getInvoiceLogs, { loading: invoiceLoading }] = useLazyQuery<
    { getInvoiceLogs: { logs: LogOutput[]; } },
    {
      pagination: PaginationInfo;
      filter: InvoiceLogsFilter;
      marketplaceId: number;
    }
  >(GET_INVOICE_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        const rows = _data.getInvoiceLogs.logs;
        setData(rows);
        setTotalRows(rows.length < logsPerPage ? (index - 1) * logsPerPage + rows.length : index * logsPerPage + MAX_ROWS);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });

  const [getShopLogs, { loading: shopLoading }] = useLazyQuery<
    { getShopLogs: { logs: LogOutput[]; } },
    {
      pagination: PaginationInfo;
      filter: ShopLogsFilter;
      marketplaceId: number;
    }
  >(GET_SHOP_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        const rows = _data.getShopLogs.logs;
        setData(rows);
        setTotalRows(rows.length < logsPerPage ? (index - 1) * logsPerPage + rows.length : index * logsPerPage + MAX_ROWS);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });
  const [getOldInvoiceLogs, { loading: oldInvoiceLoading }] = useLazyQuery<
    { getOldInvoiceLogs: { logs: LogOutput[]; total: number } },
    {
      pagination: PaginationInfo;
      filter: InvoiceLogsFilter;
      marketplaceId: number;
    }
  >(GET_OLD_INVOICE_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        setData(_data.getOldInvoiceLogs.logs);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });
  const [getOldShopLogs, { loading: oldShopLoading }] = useLazyQuery<
    { getOldShopLogs: { logs: LogOutput[]; total: number } },
    {
      pagination: PaginationInfo;
      filter: ShopLogsFilter;
      marketplaceId: number;
    }
  >(GET_OLD_SHOP_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        setData(_data.getOldShopLogs.logs);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });

  const [startShopSync] = useMutation<PullAccountsInput>(
    syncShopAccountsMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Sync succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [restartInvoices] = useMutation<RestartInvoicesInput>(
    restartInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Restart succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [retryInvoices] = useMutation<RestartInvoicesInput>(
    retryInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Retry succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );
  const [getMarketplace] = useLazyQuery<
    { getMarketplace: Marketplace },
    { id: number }
  >(getMarketplaceQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplace) => {
      const { config } = dataMarketplace.getMarketplace;
      setMarketplaceConfig(config);
    },
    onError: (e) => {
      displayError(e.message);
    },
  });

  //#endregion

  const fetchData = async (marketplaceId: number) => {
    setButtonDisabled(true);
    switch (currentTab) {
      case LogTabPages.Invoice:
        getInvoiceLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: invoiceFilter,
            marketplaceId,
          },
        });
        break;
      case LogTabPages.Shop:
        getShopLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: shopFilter,
            marketplaceId,
          },
        });
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    const id = localStorage.getItem("marketplaceId") ||
      searchParams.get("marketplaceId");
    if (id && !isNaN(+id)) {
      const idNumber = Number(id);
      setMarketplaceId(idNumber);
      getMarketplace({ variables: { id: idNumber } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const marketplaceId =
      localStorage.getItem("marketplaceId") ||
      searchParams.get("marketplaceId");
    const name = searchParams.get("name");
    setMarketplaceName(name || "");
    if (marketplaceId && !isNaN(+marketplaceId)) {
      fetchData(+marketplaceId);
    }
  }, [searchParams, index, logsPerPage, update]);

  const customSort = (rows: any, selector: Function, direction: string) => {
    return rows.sort((rowA: any, rowB: any) => {
      let aField = selector(rowA);
      let bField = selector(rowB);

      let comparison = 0;

      if (aField && aField.props) {
        aField = JSON.stringify(aField.props);
      }

      if (bField && bField.props) {
        bField = JSON.stringify(bField.props);
      }

      if (aField > bField) {
        comparison = 1;
      } else if (aField < bField) {
        comparison = -1;
      }

      return direction === "desc" ? comparison * -1 : comparison;
    });
  };

  const setFilterState = (property: string, value: any) => {
    setInvoiceFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
    setShopFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleDisplayLog = (log: string) => {
    if (log) {
      setSelectedLog(log);
      setLogModalOpened(true);
    }
  };

  const dateSorter = () => (
    <span
      onClick={() => {
        setFilterState("isAscending", !invoiceFilter.isAscending);

        invoiceFilter.isAscending = !invoiceFilter.isAscending;

        const marketplaceId = searchParams.get("marketplaceId");
        const name = searchParams.get("name");
        setMarketplaceName(name || "");
        if (marketplaceId && !isNaN(+marketplaceId)) {
          fetchData(+marketplaceId);
        }
      }}
    >
      <small
        className={
          invoiceFilter.isAscending ? "icon-angle-up" : "icon-angle-down"
        }
      />
      &nbsp;Date
    </span>
  );

  const handleRowClick = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.info(`Copied \`${value}\` to clipboard.`, {
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const columns: TableColumn<LogOutput>[] = [
    {
      name: "Invoice",
      selector: (row) => row.invoiceId!,
      maxWidth: "50px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Shop",
      selector: (row) => row.shopId,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: "Channel",
      selector: (row) => row.channelCode,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: dateSorter(),
      selector: (row) => dayjs(row.date).format("D/MMM/YYYY HH:mm:ss"),
      maxWidth: "150px",
      minWidth: "80px",
      cell: (row) =>
        row &&
        row.date && (
          <span title={dayjs(row.date).format("D/MMM/YYYY HH:mm:ss")}>
            <p
              onClick={() => {
                handleRowClick(dayjs(row.date).format("D/MMM/YYYY HH:mm:ss"));
              }}
            >
              {dayjs(row.date).format("D/MMM/YYYY HH:mm:ss")}
            </p>
          </span>
        ),
    },
    {
      name: "Type",
      selector: (row) => row.type!,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: "Status",
      cell: (row) =>
        currentTab === LogTabPages.Invoice
          ? invoiceStatusValues.find((s) => s.value === row.status)?.label
          : shopStatusValues.find((s) => s.value === row.status)?.label,
      maxWidth: "150px",
      minWidth: "80px",
    },
    {
      name: "Reason",
      cell: (row) =>
        row.reason && (
          <span
            className="row-reason-cell"
            onClick={() => handleDisplayLog(row.reason!)}
            title={row.reason}
          >
            <p>{row.reason}</p>
            <small title="Display more..." className="icon-new-tab" />
          </span>
        ),
      maxWidth: "250px",
      minWidth: "80px",
    },
    {
      name: "Request",
      selector: (row) => row.requestId!,
      maxWidth: "50px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Invocation",
      maxWidth: "250px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
      cell: (row) =>
        row &&
        row.invocationId && (
          <span title={row.invocationId.toString()}>
            <p
              onClick={() => {
                handleRowClick(row.invocationId!.toString());
              }}
            >
              {row.invocationId}
            </p>
          </span>
        ),
    },
    {
      name: "Retries remaining",
      selector: (row) => row.retryCount!,
      minWidth: "80px",
      maxWidth: "100px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Email sent",
      selector: (row) => (!!row.emailSent ? "YES" : ""),
      maxWidth: "10px",
      minWidth: "80px",
    },
    {
      name: "Email content",
      //selector: (row) => row.emailTextContent?.toString()!,
      cell: (row) =>
        row.emailTextContent && (
          <span
            className="row-reason-cell"
            onClick={() => handleDisplayLog(row.emailTextContent!)}
            title={row.emailTextContent}
          >
            <p>{row.emailTextContent}</p>
            <small title="Display more..." className="icon-new-tab" />
          </span>
        ),
      maxWidth: "200px",
      minWidth: "80px",
    },
  ];

  const changeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setLogsPerPage(currentRowsPerPage);
    setIndex(currentPage);
  };

  const changePage = (page: number) => {
    setIndex(page);
  };

  const invoiceStatusValues = Object.entries(InvoiceStatus).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const shopStatusValues = Object.entries(ShopSyncStatusEnum).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const shopTypeValues = Object.entries(ShopSyncEntityType).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const invoiceTypeValues = Object.entries(InvoiceOperationEnum).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const handleSearch = (searchParam: string, searchValue: string) => {
    setFilterState(searchParam, Number(searchValue));
  };

  const resetFields = () => {
    setInvoiceFilter({ isAscending: false, fromDate: oneMonthBefore });
    setShopFilter({ isAscending: false, fromDate: oneMonthBefore });
    setIndex(0); // to update the table
    setIndex(1);
    setResetPaginationToggle(!resetPaginationToggle);
    setIsAllowOldRecordsActive(false);
    setIsOldRecordsActive(false);
    (selectinvoiceRef.current as any)?.clearValue();
    (selectTypeRef.current as any)?.clearValue();
    if (currentTab === LogTabPages.Invoice)
      (invoiceRef.current as any).value = "";
    if (currentTab === LogTabPages.Shop)
      (shopRef.current as any).value = "";
    setUpdate(new Date());
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const spans = document.querySelectorAll('.rdt_Pagination span');
      spans.forEach((span) => {
        const text = span.textContent;
        if (text && /of\s+\d+/.test(text)) {
          const range = text.replace(/of\s+\d+/, '').trim();
          const firstRange = range.split('-');
          const page = parseInt(firstRange[0].trim(), 10);
          span.textContent = "Page " + Math.ceil(page / logsPerPage).toString();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [logsPerPage]);

  useEffect(() => {
    if (!invoiceLoading) {
      invoiceTimer.current = setTimeout(() => setShowInvoiceTable(true), DELAY);
    } else {
      setShowInvoiceTable(false);
      if (invoiceTimer.current) {
        clearTimeout(invoiceTimer.current);
        invoiceTimer.current = null;
      }
    }
    return () => {
      if (invoiceTimer.current) {
        clearTimeout(invoiceTimer.current);
        invoiceTimer.current = null;
      }
    };
  }, [invoiceLoading]);

  useEffect(() => {
    if (!shopLoading) {
      shopTimer.current = setTimeout(() => setShowShopTable(true), DELAY);
    } else {
      setShowShopTable(false);
      if (shopTimer.current) {
        clearTimeout(shopTimer.current);
        shopTimer.current = null;
      }
    }
    return () => {
      if (shopTimer.current) {
        clearTimeout(shopTimer.current);
        shopTimer.current = null;
      }
    };
  }, [shopLoading]);

  const sharedDataTable = () => (
    <>
      <DataTable
        sortFunction={customSort}
        pagination
        paginationServer
        paginationPerPage={logsPerPage}
        paginationTotalRows={totalRows}
        paginationDefaultPage={index}
        paginationResetDefaultPage={resetPaginationToggle}
        onChangeRowsPerPage={changeRowsPerPage}
        onChangePage={changePage}
        columns={columns}
        data={data}
        style={{ tableLayout: "fixed", width: "100%" }}
      />

      <div className="buttons-wrapper">
        <Button
          className="button--bordered button--grey"
          type="button"
          content="View Configuration"
          icon="logs"
          onClick={() => {
            const marketplaceId =
              localStorage.getItem("marketplaceId") ||
              searchParams.get("marketplaceId") || 0;
            navigate({
              pathname: getRoutePath(AdyenRoutesName.EDIT_MARKETPLACE),
              search: `${createSearchParams({
                marketplaceId: marketplaceId.toString(),
                name: marketplaceName,
              })}`,
            });
          }}
        ></Button>
      </div>
    </>
  );

  const handleShopSync = (ids: string) => {
    const shopIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && shopIds.length) {
      setDisabled(true);
      startShopSync({
        variables: { params: { marketplaceId, shopIds } },
      });
    }
  };

  const handleRestartInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      restartInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  const handleRetryInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      retryInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  const handleLoadOldRecordsOpened = () => {
    setData([]);
    setIsOldRecordsActive(true);
    setInvoiceFilter({ isAscending: false, fromDate: undefined });
    const marketplaceIdString =
      localStorage.getItem("marketplaceId") ||
      searchParams.get("marketplaceId");
    const marketplaceId = marketplaceIdString ? +marketplaceIdString : 0;
    switch (currentTab) {
      case LogTabPages.Invoice:
        getOldInvoiceLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: { isAscending: false, fromDate: undefined },
            marketplaceId,
          },
        });
        break;
      case LogTabPages.Shop:
        getOldShopLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: { isAscending: false, fromDate: undefined },
            marketplaceId,
          },
        });
        break;
      default:
        return;
    }
  }

  useEffect(() => {
    if (isAllowOldRecordsActive) {
      setIsAllowOldRecordsModalOpened(true)
    } else {
      setMaxDate(today)
      setMinDate(oneMonthBefore)
      const marketplaceId =
        localStorage.getItem("marketplaceId") ||
        searchParams.get("marketplaceId");
      if (marketplaceId && !isNaN(+marketplaceId)) {
        setData([]);
        setInvoiceFilter({ isAscending: false, fromDate: oneMonthBefore });
        fetchData(+marketplaceId);
      }
    }
  }, [isAllowOldRecordsActive])

  useEffect(() => {
    if (isOldRecordsActive) {
      setMaxDate(oneMonthBefore);
      setMinDate(undefined)
    } else {
      setMaxDate(today)
      setMinDate(oneMonthBefore)
    }
  }, [isOldRecordsActive])

  return (
    <main className="main-container-wrapper">
      <section className="main-container">
        <header className="main-container__header">
          <h2 className="main-container__title">
            {marketplaceName} {LogTabPages[currentTab]} Logs
          </h2>
        </header>
        <div className="main-content marketplace-logs-container">
          {currentTab !== LogTabPages.Activity && (<aside className="main-content__sidebar">
            <header className="main-content__sidebar__header">
              <h4 className="main-content__sidebar__title">Filters</h4>
              <Button
                className="button--small button--rounded button--no-wrap"
                onClick={resetFields}
                type="button"
                content="RESET ALL"
              ></Button>
            </header>
            <div className="filters">
              <Dropdown
                innerRef={selectinvoiceRef}
                options={
                  currentTab === LogTabPages.Invoice
                    ? invoiceStatusValues
                    : shopStatusValues
                }
                placeholder="Select status"
                label="Status"
                className="filters-report-page"
                isMulti={false}
                onChange={(e: any) => e && setFilterState("status", e.value)}
              />
              <Dropdown
                innerRef={selectTypeRef}
                options={currentTab === LogTabPages.Shop ? shopTypeValues : invoiceTypeValues}
                placeholder="Select type"
                label="Type"
                className="filters-report-page"
                isMulti={false}
                onChange={(e: any) => e && setFilterState("type", e.value)}
              />
              <div className="report-datepicker-container">
                <label htmlFor="report">From</label>
                <div className="report-datepicker-input-container">
                  <DatePicker
                    selected={invoiceFilter.fromDate}
                    placeholderText="Select a date"
                    onChange={(date) => setFilterState("fromDate", date)}
                    maxDate={maxDate}
                    minDate={minDate}
                  />
                </div>
              </div>
              <div className="report-datepicker-container">
                <label htmlFor="report">To</label>
                <div className="report-datepicker-input-container">
                  <DatePicker
                    selected={invoiceFilter.toDate}
                    placeholderText="Select a date"
                    onChange={(date) => setFilterState("toDate", date)}
                    maxDate={maxDate}
                    minDate={minDate}
                  />
                </div>
              </div>
              <div className="logs-id-filters">
                {currentTab === LogTabPages.Invoice && (
                  <span>
                    <InputField
                      innerRef={invoiceRef}
                      numeric
                      label="Invoice Id"
                      defaultValue={invoiceFilter.invoiceId}
                      onChange={(e) =>
                        handleSearch("invoiceId", e.currentTarget.value)
                      }
                    />
                  </span>
                )}
                <span>
                  <InputField
                    innerRef={shopRef}
                    numeric
                    label="Shop Id"
                    defaultValue={invoiceFilter.shopId}
                    onChange={(e) => {
                      handleSearch("shopId", e.currentTarget.value);
                    }}
                  />
                </span>
                <span className="logs-old-filter">
                  <p className="loadOldRecords">Load old records</p>
                  <ToggleButton
                    onChange={() => {
                      setIsAllowOldRecordsActive(!isAllowOldRecordsActive);
                    }}
                    name="loadOldRecords"
                    checked={isAllowOldRecordsActive}
                    checkedText="   "
                    uncheckedText="   "

                  />
                </span>

                <Button
                  className="button--primary"
                  content="Apply Filters"
                  onClick={() => setUpdate(new Date())}
                  disabled={buttonDisabled}
                />

              </div>
            </div>
          </aside>)}
          <div className="main-content__result marketplace-logs-table">
            <Tabs onSelect={() => resetFields()}>
              <TabList>
                <Tab
                  key={LogTabPages.Invoice}
                  onClick={() => setCurrentTab(LogTabPages.Invoice)}
                >
                  {LogTabPages[LogTabPages.Invoice]} Logs
                </Tab>
                <Tab
                  key={LogTabPages.Shop}
                  onClick={() => setCurrentTab(LogTabPages.Shop)}
                >
                  {LogTabPages[LogTabPages.Shop]} Logs
                </Tab>
                {isAuthorized && (<Tab
                  key={LogTabPages.Activity}
                  onClick={() => setCurrentTab(LogTabPages.Activity)}
                >
                  {LogTabPages[LogTabPages.Activity]}
                </Tab>)}
              </TabList>
              <TabPanel>
                {!invoiceLoading || !oldInvoiceLoading ? (
                  showInvoiceTable ? (
                    sharedDataTable()
                  ) : (
                    <div className="adyen-loader" />
                  )) : (
                  <div className="adyen-loader" />
                )}
              </TabPanel>

              <TabPanel>
                {!shopLoading || !oldShopLoading ? (
                  showShopTable ? (
                    sharedDataTable()
                  ) : (
                    <div className="adyen-loader" />
                  )) : (
                  <div className="adyen-loader" />
                )}
              </TabPanel>
              <TabPanel>
                <ControlSyncs
                  disabled={disabled}
                  config={marketplaceConfig.account}
                  handleShopSync={handleShopSync}
                  handleRetryInvoices={handleRetryInvoices}
                  handleRestartInvoices={handleRestartInvoices}
                  marketplaceId={marketplaceId}
                />
              </TabPanel>
            </Tabs>
          </div>
        </div>
      </section>
      <Modal
        isOpened={logModalOpened}
        title="Detail"
        setIsOpenedCallback={() => setLogModalOpened(false)}
        className="logs-reason-modal"
      >
        <TextArea
          className="logs-reason-details"
          value={selectedLog}
          readOnly
        />
      </Modal>

      <Modal
        isOpened={isAllowOldRecordsModalOpened}
        className="delete-user"
        title="Load old register"
        setIsOpenedCallback={() => setIsAllowOldRecordsModalOpened(false)}
      >
        <p>
          Are you sure you want to load old registers?
        </p>
        <p>This action may take a few minutes due to the high number of records.</p>
        <div className="modal-buttons-container">
          <Button
            className="button--bordered button--error"
            type="button"
            content="Cancel"
            onClick={() => {
              setIsAllowOldRecordsModalOpened(false)
              setIsAllowOldRecordsActive(!isAllowOldRecordsActive)
            }}
          ></Button>
          <Button
            className="button--bordered button--green"
            type="button"
            content="Load"
            onClick={() => {
              try {
                handleLoadOldRecordsOpened();
              } catch (e) {
                toast.error("You cannot load these records!");
              }
              setIsAllowOldRecordsModalOpened(false);
            }}
          ></Button>
        </div>
      </Modal>
    </main>
  );
};
